import {
  Box,
  Button,
  FormControl,
  Icon,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { ChangeEvent, useEffect, useMemo, useState, MouseEvent } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import {
  useGetQualita,
  useQualitaUpdate,
} from "../../../effects/lotto/attributes/qualita";
import { QualitaUpdateFormData } from "../../../interfaces/lotto/attributes/forms";
import { useAppSelector } from "../../../store/hooks";
import ImageUpload from "./imageUpload";
import Thumbnail from "./thumbnail";
import PublishDialog from "./dialogs/publishDialog";
import { IImage } from "../../../interfaces/lotto/attributes/models";
import DeleteDialog from "./dialogs/deleteDialog";
import UploadExtDialog from "./dialogs/upload/uploadExtDialog";

const i18_PATH = "qualita.update.";

function Update() {
  const navigate = useNavigate();
  const user = useAppSelector((state) => state.auth.user);
  const { t } = useTranslation();
  const [query, setQuery] = useState<number>(1);
  const [formData, setFormData] = useState<QualitaUpdateFormData>(null);
  const { id } = useParams();
  const qualitaId = !!id ? parseInt(id) : null;
  const qualita = useAppSelector(
    (state) => state.lotto_qualita.itemsExt?.[parseInt(id ?? "")]
  );

  const [publishOnTheWebDialogOpen, setPublishOnTheWebDialogOpen] =
    useState<boolean>(false);
  const [selectedImage, setSelectedImage] = useState<IImage | null>(null);
  const [isDeleteOpen, setIsDeleteOpen] = useState<boolean>(false);
  const [isUploadExtOpen, setIsUploadExtOpen] = useState<boolean>(false);

  const [nome, setNome] = useState<string>("");
  const [idMateriale, setIdMateriale] = useState<string>("");
  const [idFondo, setIdFondo] = useState<string>("");
  const [utilizzo, setUtilizzo] = useState<string[]>([]);
  const [finiture, setFiniture] = useState<string[]>([]);
  const [densita, setDensita] = useState<string>("");
  const [resistenzaCompressione, setResistenzaCompressione] =
    useState<string>("");
  const [resistenzaFlessione, setResistenzaFlessione] = useState<string>("");
  const [coefImbibizione, setCoefImbibizione] = useState<string>("");
  const [descrizione, setDescrizione] = useState<string>("");

  const canPublish = useMemo(
    () => !!user?.admin || !!user?.user_group?.group_permissions?.canPublish,
    [user]
  );
  const onPublishOnTheWeb = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setPublishOnTheWebDialogOpen(true);
  };

  const { items: materialeItems, list: materialeList } = useAppSelector(
    (state) => state.lotto_materiale
  );
  const { items: fondoItems, list: fondoList } = useAppSelector(
    (state) => state.lotto_fondo
  );
  const { items: usoItems, list: usoList } = useAppSelector(
    (state) => state.lotto_uso
  );
  const { items: statoLavorazioneItems, list: statoLavorazioneList } =
    useAppSelector((state) => state.dettaglio_stato_lavorazione);

  const lastActionDone = useAppSelector((state) => state.ui.lastActionDone);

  const materialeOptions = useMemo(
    () =>
      !!materialeList && !!materialeItems
        ? materialeList.map((item) => materialeItems[item])
        : [],
    [materialeItems, materialeList]
  );
  const fondoOptions = useMemo(
    () =>
      !!fondoList && !!fondoItems
        ? fondoList.map((item) => fondoItems[item])
        : [],
    [fondoItems, fondoList]
  );
  const usoOptions = useMemo(
    () =>
      !!usoList && !!usoItems ? usoList.map((item) => usoItems[item]) : [],
    [usoItems, usoList]
  );
  const statoLavorazioneOptions = useMemo(
    () =>
      !!statoLavorazioneList && !!statoLavorazioneItems
        ? statoLavorazioneList.map((item) => statoLavorazioneItems[item])
        : [],
    [statoLavorazioneItems, statoLavorazioneList]
  );
  const onUtilizzoChange = (event: SelectChangeEvent<string[]>) => {
    const value = Array.isArray(event.target.value)
      ? event.target.value
      : [event.target.value];
    setUtilizzo(value);
  };
  const onFinitureChange = (event: SelectChangeEvent<string[]>) => {
    const value = Array.isArray(event.target.value)
      ? event.target.value
      : [event.target.value];
    setFiniture(value);
  };
  const onDensitaChange = (event: ChangeEvent<HTMLInputElement>) => {
    setDensita(event.target.value);
  };
  const onResistenzaCompressioneChange = (
    event: ChangeEvent<HTMLInputElement>
  ) => {
    setResistenzaCompressione(event.target.value);
  };
  const onResistenzaFlessioneChange = (
    event: ChangeEvent<HTMLInputElement>
  ) => {
    setResistenzaFlessione(event.target.value);
  };
  const onCoefImbibizioneChange = (event: ChangeEvent<HTMLInputElement>) => {
    setCoefImbibizione(event.target.value);
  };
  const onDescrizioneChange = (event: ChangeEvent<HTMLInputElement>) => {
    setDescrizione(event.target.value);
  };

  const submit = () => {
    setFormData({
      utilizzo: utilizzo.map((id) => parseInt(id)),
      finiture: finiture.map((id) => parseInt(id)),
      densita: parseFloat(densita) || undefined,
      resistenzaCompressione: parseFloat(resistenzaCompressione) || undefined,
      resistenzaFlessione: parseFloat(resistenzaFlessione) || undefined,
      coefImbibizione: parseFloat(coefImbibizione) || undefined,
      descrizione,
    });
  };

  const openDelete = (image: IImage) => {
    setSelectedImage(image);
    setIsDeleteOpen(true);
  };

  const closeDelete = () => {
    setSelectedImage(null);
    setIsDeleteOpen(false);
  };

  const openUploaExtdDialog = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setIsUploadExtOpen(true);
  };

  const closeExtUpload = () => {
    setIsUploadExtOpen(false);
  };

  const back = () => {
    navigate("..");
  };
  useQualitaUpdate(qualitaId, formData);
  useGetQualita(qualitaId, query);
  useEffect(() => {
    if (qualita?.nome) setNome(qualita.nome);
    if (qualita?.ext?.descrizione) setDescrizione(qualita.ext.descrizione);
    if (qualita?.idMateriale) setIdMateriale(String(qualita.idMateriale));
    if (qualita?.idFondo) setIdFondo(String(qualita.idFondo));
    if (qualita?.finiture)
      setFiniture(qualita.finiture.map((item) => String(item.id)));
    if (qualita?.utilizzo)
      setUtilizzo(qualita.utilizzo.map((item) => String(item.id)));
    if (qualita?.ext?.densita) setDensita(String(qualita.ext.densita));
    if (qualita?.ext?.resistenzaCompressione)
      setResistenzaCompressione(String(qualita.ext.resistenzaCompressione));
    if (qualita?.ext?.resistenzaFlessione)
      setResistenzaFlessione(String(qualita.ext.resistenzaFlessione));
    if (qualita?.ext?.coefImbibizione)
      setCoefImbibizione(String(qualita.ext.coefImbibizione));
  }, [qualita]);
  useEffect(() => {
    if (lastActionDone === "upload-immagine-qualita") {
      setQuery(query + 1);
    }
    if (lastActionDone === "update-publish-web-qualita") {
      setPublishOnTheWebDialogOpen(false);
    }
    if (lastActionDone === "upload-ext-immagine-qualita") {
      setIsUploadExtOpen(false);
      setQuery(query + 1);
    }
    if (lastActionDone === "remove-ext-immagine-qualita") {
      setSelectedImage(null);
      setIsDeleteOpen(false);
      setQuery(query + 1);
    }
  }, [lastActionDone, query]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        backgroundColor: "#fff",
      }}
    >
      <Box sx={{ flex: 1, position: "relative" }}>
        <Box
          sx={{
            backgroundColor: "#fff",
            boxSizing: "border-box",
            position: "absolute",
            height: "100%",
            width: "100%",
            overflowY: "auto",
          }}
        >
          <Box sx={{ p: 2, display: "flex" }}>
            <Typography sx={{ flex: 1 }} variant="subtitle1">
              Modifica Qualità
            </Typography>
            <Box sx={{ mr: 2 }}>
              {canPublish && qualita?.ext?.pubblicatoSuWeb && (
                <Tooltip title={t(`${i18_PATH}Web`)}>
                  <IconButton onClick={onPublishOnTheWeb} color="success">
                    <Icon
                      className="fa-solid fa-globe-pointer"
                      sx={{ overflow: "visible" }}
                    />
                  </IconButton>
                </Tooltip>
              )}
              {canPublish && !qualita?.ext?.pubblicatoSuWeb && (
                <Tooltip title={t(`${i18_PATH}Web`)}>
                  <IconButton onClick={onPublishOnTheWeb}>
                    <Icon
                      className="fa-light fa-globe-pointer"
                      sx={{ overflow: "visible" }}
                    />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </Box>
          <Box sx={{ display: "flex" }}>
            <Box sx={{ px: 2, py: 0.5 }}>
              <Box sx={{ height: 400, width: 400 }}>
                <ImageUpload qualita={qualita} />
              </Box>
            </Box>
            <Box sx={{ flex: 1, px: 2, py: 0.5, gap: 1 }}>
              <Box sx={{ display: "flex", px: 2, py: 0.5, gap: 1 }}>
                <TextField
                  sx={{ flex: 1 }}
                  margin="dense"
                  label={t(`${i18_PATH}Nome`)}
                  type="text"
                  value={nome}
                  disabled
                />
                <FormControl sx={{ flex: 1, mt: 1, mb: 0.5 }}>
                  <InputLabel id="materiale_label">
                    {t(`${i18_PATH}Materiale`)}
                  </InputLabel>
                  <Select
                    labelId="materiale_label"
                    id="materiale_select"
                    value={idMateriale}
                    label={t(`${i18_PATH}Materiale`)}
                    disabled
                  >
                    {materialeOptions.map((item) => (
                      <MenuItem key={String(item.id)} value={item.id}>
                        {item.nome}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl sx={{ flex: 1, mt: 1, mb: 0.5 }}>
                  <InputLabel id="fondo_label">
                    {t(`${i18_PATH}Colore`)}
                  </InputLabel>
                  <Select
                    labelId="fondo_label"
                    id="fondo_select"
                    value={idFondo}
                    label={t(`${i18_PATH}Colore`)}
                    disabled
                  >
                    {fondoOptions.map((item) => (
                      <MenuItem key={String(item.id)} value={item.id}>
                        {item.nome}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              <Box sx={{ display: "flex", px: 2, py: 0.5, gap: 1 }}>
                <TextField
                  margin="dense"
                  sx={{ flex: 1 }}
                  label={t(`${i18_PATH}Densita`)}
                  type="number"
                  value={densita}
                  onChange={onDensitaChange}
                />
                <TextField
                  margin="dense"
                  sx={{ flex: 1 }}
                  label={t(`${i18_PATH}ResistenzaCompressione`)}
                  type="number"
                  value={resistenzaCompressione}
                  onChange={onResistenzaCompressioneChange}
                />
                <TextField
                  margin="dense"
                  sx={{ flex: 1 }}
                  label={t(`${i18_PATH}ResistenzaFlessione`)}
                  type="number"
                  value={resistenzaFlessione}
                  onChange={onResistenzaFlessioneChange}
                />
                <TextField
                  margin="dense"
                  sx={{ flex: 1 }}
                  label={t(`${i18_PATH}CoefImbibizione`)}
                  type="number"
                  value={coefImbibizione}
                  onChange={onCoefImbibizioneChange}
                />
              </Box>
              <Box sx={{ display: "flex", px: 2, py: 0.5, gap: 1 }}>
                <Box
                  sx={{
                    display: "flex",
                    flex: 1,
                    flexDirection: "column",
                    gap: 1,
                  }}
                >
                  <FormControl sx={{ flex: 1, mt: 1, mb: 0.5 }}>
                    <InputLabel id="utilizzo_label">
                      {t(`${i18_PATH}Utilizzo`)}
                    </InputLabel>
                    <Select
                      labelId="utilizzo_label"
                      id="utilizzo_select"
                      value={utilizzo}
                      multiple={true}
                      label={t(`${i18_PATH}Utilizzo`)}
                      onChange={onUtilizzoChange}
                    >
                      {usoOptions.map((item) => (
                        <MenuItem key={String(item.id)} value={item.id}>
                          {item.nome}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl sx={{ flex: 1, mt: 1, mb: 0.5 }}>
                    <InputLabel id="finiture_label">
                      {t(`${i18_PATH}Finiture`)}
                    </InputLabel>
                    <Select
                      labelId="finiture_label"
                      id="finiture_select"
                      value={finiture}
                      multiple={true}
                      label={t(`${i18_PATH}Finiture`)}
                      onChange={onFinitureChange}
                    >
                      {statoLavorazioneOptions.map((item) => (
                        <MenuItem key={String(item.id)} value={item.id}>
                          {item.nome}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
                <TextField
                  sx={{ flex: 2 }}
                  margin="dense"
                  label={t(`${i18_PATH}Descrizione`)}
                  type="text"
                  value={descrizione}
                  onChange={onDescrizioneChange}
                  multiline
                  rows={5}
                />
              </Box>
              {!!qualita && (
                <Box sx={{ px: 2, py: 0.5 }}>
                  <Box sx={{ display: "flex", alignItems: "center", pb: 1 }}>
                    <Typography variant="subtitle1">Altre Immagini</Typography>
                    <Tooltip title={t(`${i18_PATH}UploadExtImages`)}>
                      <IconButton onClick={openUploaExtdDialog}>
                        <Icon
                          className="fa-light fa-plus-circle"
                          sx={{ overflow: "visible" }}
                        />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  <Box>
                    {!!qualita.ext_images &&
                      qualita.ext_images.map((image) => (
                        <Box sx={{ width: 300 }}>
                          <Thumbnail
                            image={image}
                            onDelete={openDelete}
                          ></Thumbnail>
                        </Box>
                      ))}
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          padding: 2,
          border: "1px solid rgba(224, 224, 224, 1)",
        }}
      >
        <Button onClick={back}>{t(`${i18_PATH}Cancella`)}</Button>
        <Button variant="contained" onClick={submit}>
          {t(`${i18_PATH}Salva`)}
        </Button>
      </Box>
      {!!qualita && (
        <PublishDialog
          qualita={qualita}
          isOpen={publishOnTheWebDialogOpen}
          close={() => setPublishOnTheWebDialogOpen(false)}
        />
      )}
      <DeleteDialog
        isOpen={isDeleteOpen}
        image={selectedImage}
        close={closeDelete}
      />
      {!!qualita && (
        <UploadExtDialog
          idQualita={qualita.id}
          isOpen={isUploadExtOpen}
          close={closeExtUpload}
        />
      )}
    </Box>
  );
}

export default Update;
